import { companyRequest } from '@/company/api';
import { savedArtifactViewSchema } from '../../../../src/artifacts/saved-view-contract.mjs';
import { artifactApi } from '../../artifacts/api';
import { AppSharingChip } from '../sharing-chip';
import {
  savedItemTarget,
  type SavedItem,
  type SaveToLibraryWriter,
} from '../../library/save-to-library';
import type { CanvasTarget } from '../../types';
import { readInitialCollectionIds } from '../../../../src/apps/tracer/collection-inventory.mjs';

type Saved = {
  companyId: string;
  appId: string;
  artifactId: string;
  versionId: string;
  revisionId: string;
};
export type SaveAppAnswer = { saved: Saved; outputAvailable: boolean };

const text = (value: unknown) => (typeof value === 'string' && value.length > 0 ? value : null);
/** The strict answer of the save callable: the genuine saved tuple and whether its output exists. */
export function parseSaveApp(value: unknown): SaveAppAnswer | null {
  const raw = value as { saved?: Record<string, unknown>; outputAvailable?: unknown } | null;
  const saved = raw?.saved;
  if (!saved || typeof raw?.outputAvailable !== 'boolean') return null;
  const [companyId, appId, artifactId, versionId, revisionId] = [
    text(saved.companyId),
    text(saved.appId),
    text(saved.artifactId),
    text(saved.versionId),
    text(saved.revisionId),
  ];
  return companyId && appId && artifactId && versionId && revisionId
    ? {
        saved: { companyId, appId, artifactId, versionId, revisionId },
        outputAvailable: raw.outputAvailable,
      }
    : null;
}

/**
 * The App writer behind the shared Save to Library control: the server resolves the owner, the private thread and its
 * source; the browser supplies the conversation, optional title and bounded collection declarations. A confirmed save is the answer of this ONE
 * call; reading the saved version (for its number) belongs to Open, so a failed read never un-saves it.
 */
export function createAppSaveWriter({
  threadId,
  title,
  send,
  readInventory,
}: {
  threadId: string;
  title: string;
  send: typeof fetch;
  readInventory: () => Promise<readonly string[]>;
}): SaveToLibraryWriter {
  return {
    async save() {
      const initialCollectionIds = readInitialCollectionIds(await readInventory());
      const answer = parseSaveApp(
        await companyRequest<unknown>(
          `/api/v1/company/threads/${encodeURIComponent(threadId)}/app/save`,
          'POST',
          { title, initialCollectionIds },
          send,
        ),
      );
      if (!answer) throw new Error('save_app_unrecognized');
      const { artifactId, versionId, revisionId } = answer.saved;
      return {
        saved: { artifactId, versionId, revisionId },
        outputAvailable: answer.outputAvailable,
      };
    },
  };
}

/**
 * Open a saved App in its existing Library canvas: read the saved version by the tuple the writer returned (its number),
 * verify it is that version, then open it. A failure here leaves the item Saved; only this read is retried.
 */
export async function openSavedApp(
  saved: SavedItem,
  send: typeof fetch,
  open: (target: CanvasTarget) => void,
): Promise<void> {
  const view = savedArtifactViewSchema.parse(
    await artifactApi.readVersion(
      { versionId: saved.versionId, purpose: saved.purpose ?? 'editor' },
      send,
    ),
  );
  if (view.artifactId !== saved.artifactId || view.versionId !== saved.versionId)
    throw new Error('saved_version_mismatch');
  const target = savedItemTarget({
    ...saved,
    versionNumber: view.number,
    revisionId: view.revisionId,
  });
  if (!target) throw new Error('saved_target_unavailable');
  open(target);
}

/** The preview's Share chip binds only its genuine completed Save, never a live preview id. */
export function AppSaveControl({
  itemKey,
  saved,
  title,
}: {
  itemKey: string;
  saved?: SavedItem | null;
  /** Kept for the preview's existing caller; there is no manual Save control. */
  threadId?: string;
  title?: string;
  readInventory?: () => Promise<readonly string[]>;
}) {
  if (!saved) return null;
  return (
    <AppSharingChip
      key={`${itemKey}:${saved.artifactId}:${saved.versionId}:${saved.revisionId}`}
      selection={saved}
      title={title}
    />
  );
}
